// #CUST_ALIAS_V1 (2026-09-03) — Bill: duplicate customer names (JC Comm / JC Communications, and
// others) were fragmenting rollups across Job Cost, Estimating and Revenue — the same customer
// showing as two+ separate rows because of how it was typed on different jobs over the years.
// Same non-destructive pattern as ForkWorklist's DUC fork-alias (src/ForkWorklist.js): map a
// raw-as-typed name to one canonical spelling in Supabase key `customer-alias`
// ({ [normalizedRawName]: canonicalName }). Nothing on the underlying Job List / cost-history /
// Revenue records is rewritten — every rollup just resolves display/grouping through this map, and
// Bill can add more aliases himself at any time a new variant turns up (see CustomerAliasTool.js).

export const CUSTOMER_ALIAS_KEY = 'customer-alias';

// Normalize a name for use as a lookup key: trim, collapse internal whitespace, uppercase. This
// alone folds pure case/whitespace variants ("jc comm " vs "JC Comm") into one key automatically —
// anything more different (abbreviation, punctuation) still needs an explicit alias entry.
export const normCustKey = (s) => String(s || '').trim().replace(/\s+/g, ' ').toUpperCase();

// Resolve a raw customer name to its canonical display name via the alias map. `aliasMap` is the
// raw object stored at the customer-alias key: { [normCustKey(rawName)]: canonicalName }.
// #CUST_AUTOFOLD_V1 (2026-10-12) — Bill: Job P&L customer dropdown still showed Bobcat/BOBCAT,
// Borepro/BorePro, Broad Head/Broadhead as separate customers. An exact-key miss now falls back to a
// "letters and digits only" key, matched against both the alias keys AND the canonical names, so
// once any one spelling of a customer is merged, every case/space/punctuation variant of it follows
// automatically — no per-variant entry needed. Index is cached per alias-map object.
const looseCustKey = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const _looseIdx = new WeakMap();
const _looseIndexFor = (aliasMap) => {
  let idx = _looseIdx.get(aliasMap);
  if (idx) return idx;
  idx = new Map();
  Object.entries(aliasMap).forEach(([k, canon]) => {
    const c = String(canon || '').trim(); if (!c) return;
    const kc = looseCustKey(c); if (kc && !idx.has(kc)) idx.set(kc, c);   // canonical spelling of itself
  });
  Object.entries(aliasMap).forEach(([k, canon]) => {
    const c = String(canon || '').trim(); if (!c) return;
    const kk = looseCustKey(k); if (kk && !idx.has(kk)) idx.set(kk, c);   // variant key -> canonical
  });
  _looseIdx.set(aliasMap, idx);
  return idx;
};
export const resolveCustomer = (aliasMap, rawName) => {
  const raw = String(rawName || '').trim();
  if (!raw) return raw;
  if (!aliasMap || typeof aliasMap !== 'object') return raw;
  const hit = aliasMap[normCustKey(raw)];
  if (hit) return hit;
  const loose = _looseIndexFor(aliasMap).get(looseCustKey(raw));   // #CUST_AUTOFOLD_V1
  return loose || raw;
};

// #CUST_PICKER_V1 (2026-09-03) — Bill: "How do we keep various name entries from being entered..."
// The canonical customer list for CustomerPicker's dropdown: every distinct customer already on
// the Job List, resolved through the alias map (so a merge Bill's already made collapses its
// variants into one option), deduped and sorted. `jobs` is the Job List array; each record's
// customer field is either `.cust` (App.js's own field name) or `.customer`.
export const customerOptionsFrom = (jobs, aliasMap) => {
  const set = new Set();
  (jobs || []).forEach(j => {
    const raw = (j && (j.cust || j.customer)) || '';
    const name = resolveCustomer(aliasMap, raw);
    if (name) set.add(name);
  });
  return [...set].sort((a, b) => a.localeCompare(b));
};
